import React, { useEffect, useState } from "react";
import { X, Sparkles, Loader2, Save, Plus, Trash2, FileText, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { fileUrl } from "../lib/apiExtra";
import { stateName } from "../lib/states";
import { DatePicker } from "./DatePicker";

const FIELD_LABELS = {
  type_of_search: "Type of Search", order_no: "Order No.", borrower: "Borrower", prior_owner: "Prior Owner",
  property: "Property", county: "County", state: "State", property_id: "Property ID",
  land_value: "Land Value", building_value: "Building Value", total_value: "Total Value",
};
const SIMPLE = ["type_of_search", "order_no", "borrower", "prior_owner", "property", "county", "state", "property_id", "land_value", "building_value", "total_value"];

export const SearchNoteModal = ({ orderId, onClose, onSaved, locked }) => {
  const [sn, setSn] = useState(null);
  const [layout, setLayout] = useState({ fields: SIMPLE, documents: true });
  const [loading, setLoading] = useState(true);
  const [autofilling, setAutofilling] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}/search-note/data`);
        setSn({ documents: [], ...data.search_note });
        if (data.layout) setLayout(data.layout);
      } catch { toast.error("Could not load search note"); }
      setLoading(false);
    })();
  }, [orderId]);

  const show = (k) => (layout.fields || []).includes(k);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const buildText = () => {
    const L = "*".repeat(60);
    const line = (lab, v) => `${lab}: ${v || ""}`;
    const rows = (sn.documents || []).map((d) => `${d.doc_type || ""}\t${d.recorded_date || ""}\t${d.instrument_no || ""}\t${d.book_page || ""}`).join("\n");
    return [
      "\t\tAbstract Notes", L,
      sn.order_summary ? line("Order Summary", sn.order_summary) : "",
      sn.order_summary ? L : "",
      line("Type of Search", sn.type_of_search), line("Order No", sn.order_no),
      line("Borrower", sn.borrower), show("prior_owner") && line("Prior Owner", sn.prior_owner),
      line("Property", sn.property), line("County", sn.county), line("State", stateName(sn.state)),
      line("Property ID", sn.property_id), line("Effective Date", sn.effective_date),
      show("short_legal") && line("Short Legal", sn.short_legal), L,
      show("names_searched") && `Names Searched:\n${sn.names_searched || ""}`,
      layout.documents ? `${L}\nDocument Type\tRecorded Date\tInstrument No\tBook/Page\n${rows}` : "",
      L, "\t\tEnd of Notes",
    ].filter(Boolean).join("\n");
  };

  const copyNote = async () => {
    const text = buildText();
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      toast.success("Search note copied — paste into OneDrive");
    } catch { toast.error("Copy failed"); }
  };

  const set = (k, v) => setSn((s) => ({ ...s, [k]: v }));
  const setDoc = (i, k, v) => setSn((s) => { const d = [...(s.documents || [])]; d[i] = { ...d[i], [k]: v }; return { ...s, documents: d }; });
  const addDoc = () => setSn((s) => ({ ...s, documents: [...(s.documents || []), { doc_type: "", recorded_date: "", instrument_no: "", book_page: "" }] }));
  const rmDoc = (i) => setSn((s) => ({ ...s, documents: (s.documents || []).filter((_, x) => x !== i) }));

  const autofill = async () => {
    setAutofilling(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/search-note/autofill`);
      setSn({ documents: [], ...data.search_note });
      toast.success(data.autofilled ? "AI pre-filled from client documents" : "No client documents to read yet");
    } catch { toast.error("Auto-fill failed"); }
    setAutofilling(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/orders/${orderId}/search-note/data`, { search_note: sn });
      toast.success("Search note saved");
      onSaved && onSaved(data);
      onClose();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const input = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div data-testid="search-note-modal" className="ws-light w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-600 flex items-center gap-2"><FileText className="h-4 w-4 text-teal" /> Search Note (Abstract Notes)</h3>
          <button data-testid="search-note-close" onClick={onClose} className="text-slate-500 hover:text-slate-600"><X className="h-5 w-5" /></button>
        </div>

        {loading || !sn ? (
          <div className="py-10 text-center text-sm text-slate-500">Loading…</div>
        ) : (
          <>
            {sn.order_summary ? (
              <div data-testid="search-note-order-summary" className="mb-4 rounded-md border border-teal/30 bg-teal/5 p-3">
                <div className="text-[11px] font-bold uppercase tracking-wide text-teal mb-1">Order Summary</div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{sn.order_summary}</p>
              </div>
            ) : null}
            <div className="mb-4 flex items-center gap-2">
              <a data-testid="search-note-download" href={fileUrl(`/api/orders/${orderId}/search-note`)} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                <FileText className="h-3.5 w-3.5" /> Download .txt
              </a>
              <button data-testid="search-note-copy" onClick={copyNote}
                className="inline-flex items-center gap-1.5 rounded-md border border-teal/40 bg-accent px-3 py-2 text-xs font-semibold text-accent-foreground hover:bg-teal hover:text-white transition-colors">
                <Copy className="h-3.5 w-3.5" /> Copy for OneDrive
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {SIMPLE.filter(show).map((k) => (
                <div key={k}>
                  <label className="text-xs font-semibold text-slate-500">{FIELD_LABELS[k]}</label>
                  <input data-testid={`sn-${k}`} value={sn[k] || ""} disabled={locked} onChange={(e) => set(k, e.target.value)} className={input} />
                </div>
              ))}
              {show("effective_date") && (
                <div>
                  <label className="text-xs font-semibold text-slate-500">Effective Date</label>
                  <DatePicker testid="sn-effective_date" value={sn.effective_date || ""} disabled={locked} onChange={(v) => set("effective_date", v)} />
                </div>
              )}
            </div>

            {show("short_legal") && (
              <div className="mt-3">
                <label className="text-xs font-semibold text-slate-500">Short Legal</label>
                <textarea data-testid="sn-short_legal" rows={2} value={sn.short_legal || ""} disabled={locked} onChange={(e) => set("short_legal", e.target.value)} className={input} />
              </div>
            )}
            {show("names_searched") && (
              <div className="mt-3">
                <label className="text-xs font-semibold text-slate-500">Names Searched (one per line)</label>
                <textarea data-testid="sn-names_searched" rows={2} value={sn.names_searched || ""} disabled={locked} onChange={(e) => set("names_searched", e.target.value)} className={input} />
              </div>
            )}

            {layout.documents && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-slate-500">Documents Recorded</label>
                {!locked && <button data-testid="sn-add-doc" onClick={addDoc} className="inline-flex items-center gap-1 text-xs font-semibold text-teal hover:text-navy-2"><Plus className="h-3.5 w-3.5" /> Add row</button>}
              </div>
              <div className="space-y-2">
                {(sn.documents || []).map((d, i) => (
                  <div key={i} data-testid={`sn-doc-${i}`} className="grid grid-cols-12 gap-2 items-center">
                    <input placeholder="Type" value={d.doc_type || ""} disabled={locked} onChange={(e) => setDoc(i, "doc_type", e.target.value)} className={`col-span-3 ${input}`} />
                    <input placeholder="Recorded Date" value={d.recorded_date || ""} disabled={locked} onChange={(e) => setDoc(i, "recorded_date", e.target.value)} className={`col-span-3 ${input}`} />
                    <input placeholder="Instrument No." value={d.instrument_no || ""} disabled={locked} onChange={(e) => setDoc(i, "instrument_no", e.target.value)} className={`col-span-3 ${input}`} />
                    <input placeholder="Book/Page" value={d.book_page || ""} disabled={locked} onChange={(e) => setDoc(i, "book_page", e.target.value)} className={`col-span-2 ${input}`} />
                    {!locked && <button data-testid={`sn-rm-doc-${i}`} onClick={() => rmDoc(i)} className="col-span-1 text-slate-500 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                ))}
                {(sn.documents || []).length === 0 && <p className="text-xs text-slate-500">No documents added yet.</p>}
              </div>
            </div>
            )}

            {!locked && (
              <div className="mt-5 flex justify-end">
                <button data-testid="search-note-save" onClick={save} disabled={saving}
                  className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors disabled:opacity-60">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Search Note
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
